<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <edit-employee-layout
    screen="job"
    :employee-id="empNumber"
    :max-file-size="maxFileSize"
    :allowed-file-types="allowedFileTypes"
  >
    <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('pim.job_details') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form
        id="employee-job-form"
        :loading="isLoading"
        @submit-valid="onSave"
      >
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="job.joinedDate"
                :label="$t('general.joined_date')"
                :rules="rules.joinedDate"
                :disabled="!hasUpdatePermissions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="job.jobTitleId"
                type="select"
                :label="$t('general.job_title')"
                :options="normalizedJobTitles"
                :disabled="!hasUpdatePermissions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="job.jobLevelName"
                :label="$t('general.job_level_name')"
                :disabled="true"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <job-spec-download
                :key="`jobspec-${selectedJobTitleId}`"
                :resource-id="selectedJobTitleId"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="job.jobCategoryId"
                type="select"
                :label="$t('general.job_category')"
                :options="jobCategories"
                :disabled="!hasUpdatePermissions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="job.subunitId"
                type="select"
                :label="$t('general.sub_unit')"
                :options="subunits"
                :disabled="!hasUpdatePermissions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="job.locationId"
                type="select"
                :label="$t('general.location')"
                :options="locations"
                :disabled="!hasUpdatePermissions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="job.empStatusId"
                type="select"
                :label="$t('general.employment_status')"
                :options="employmentStatuses"
                :disabled="!hasUpdatePermissions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                :model-value="workShiftDisplay"
                :label="$t('admin.work_shifts')"
                :disabled="true"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />

        <oxd-form-row class="user-form-header">
          <oxd-text class="user-form-header-text" tag="p">
            {{ $t('pim.show_employment_contract_details') }}
          </oxd-text>
          <oxd-switch-input v-model="showContractDetails" />
        </oxd-form-row>

        <template v-if="showContractDetails && !isEditingContract">
          <oxd-form-row class="contract-view-row">
            <oxd-input-group :label="$t('pim.contract_files')">
              <div class="orangehrm-file-current">
                <div
                  class="orangehrm-file-preview"
                  :class="{
                    'orangehrm-file-preview--empty': !contract.oldAttachment,
                  }"
                  @click="downloadContract"
                >
                  <oxd-icon
                    class="orangehrm-file-icon"
                    name="file-earmark-text"
                  />
                  <oxd-text
                    class="orangehrm-file-name"
                    tag="p"
                    :title="contractDocumentName"
                  >
                    {{ contractDocumentName }}
                    <oxd-icon
                      v-if="contract.oldAttachment"
                      class="orangehrm-file-download"
                      name="download"
                    />
                  </oxd-text>
                </div>
                <div class="orangehrm-file-options contract-file-meta">
                  <oxd-input-field
                    class="contract-file-meta__input"
                    :model-value="contractTypeDisplay || '—'"
                    :label="$t('pim.contract_type')"
                    readonly
                    disabled
                  />
                  <oxd-input-field
                    class="contract-file-meta__input"
                    :model-value="contractStartDateDisplay"
                    :label="$t('pim.contract_start_date')"
                    readonly
                    disabled
                  />
                  <oxd-input-field
                    class="contract-file-meta__input"
                    :model-value="contractEndDateDisplay"
                    :label="$t('pim.contract_end_date')"
                    readonly
                    disabled
                  />
                  <div
                    v-if="hasUpdatePermissions"
                    class="contract-file-meta__row contract-file-meta__row--action"
                  >
                    <oxd-text tag="p" class="contract-file-meta__label">
                      {{ $t('pim.contract_action') }}
                    </oxd-text>
                    <div class="contract-file-meta__edit">
                      <oxd-button
                        display-type="secondary"
                        icon-name="pencil"
                        :label="$t('general.edit')"
                        @click="startContractEdit"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </oxd-input-group>
          </oxd-form-row>
        </template>
      </oxd-form>

      <oxd-form
        v-if="showContractDetails && isEditingContract && hasUpdatePermissions"
        class="contract-edit-form"
        :loading="isContractSaving"
        @submit-valid="saveContract"
      >
        <oxd-form-row>
          <oxd-input-group :label="$t('pim.edit_contract')">
            <div class="contract-edit-panel">
              <oxd-grid :cols="1" class="orangehrm-full-width-grid">
                <oxd-grid-item class="contract-action-field">
                  <oxd-input-field
                    v-model="contractActionSelect"
                    type="select"
                    :label="$t('pim.contract_action')"
                    :options="contractActionOptions"
                    :rules="rules.contractAction"
                  />
                  <oxd-alert
                    v-if="contractActionDescription"
                    type="info"
                    :show="true"
                    :message="contractActionDescription"
                    class="contract-action-alert"
                  />
                </oxd-grid-item>
              </oxd-grid>
              <oxd-grid
                v-if="showsContractMetadataFields"
                :cols="3"
                class="orangehrm-full-width-grid contract-edit-panel__section"
              >
                <oxd-grid-item>
                  <oxd-input-field
                    v-model="contract.contractType"
                    type="select"
                    :label="$t('pim.contract_type')"
                    :options="contractTypeOptions"
                  />
                </oxd-grid-item>
                <oxd-grid-item>
                  <date-input
                    v-model="contract.startDate"
                    :label="$t('pim.contract_start_date')"
                    :rules="rules.startDate"
                  />
                </oxd-grid-item>
                <oxd-grid-item v-if="!isPermanentContract">
                  <date-input
                    v-model="contract.endDate"
                    :label="$t('pim.contract_end_date')"
                    :rules="rules.endDate"
                  />
                </oxd-grid-item>
              </oxd-grid>
              <template v-if="requiresContractFileUpload">
                <oxd-grid
                  :cols="2"
                  class="orangehrm-full-width-grid contract-edit-panel__section"
                >
                  <oxd-grid-item>
                    <oxd-input-field
                      v-model="contract.newAttachment"
                      type="file"
                      accept="application/pdf"
                      :label="$t('pim.contract_document')"
                      :button-label="$t('general.browse')"
                      :placeholder="$t('general.no_file_selected')"
                      :rules="rules.contractAttachment"
                    />
                    <oxd-text class="contract-file-hint" tag="p">
                      {{ $t('pim.contract_file_hint') }}
                    </oxd-text>
                  </oxd-grid-item>
                </oxd-grid>
              </template>
              <template
                v-if="contract.method === CONTRACT_METHOD_RENEW_TEMPLATE"
              >
                <div class="contract-edit-panel__section">
                  <oxd-text
                    v-if="!contractTemplates.length"
                    tag="p"
                    class="contract-template-picker__empty"
                  >
                    {{ $t('general.no_records_found') }}
                  </oxd-text>
                  <div v-else class="contract-template-picker__list">
                    <div
                      v-for="template in contractTemplates"
                      :key="template.id"
                      role="radio"
                      class="contract-template-picker__option"
                      :class="{
                        'contract-template-picker__option--selected':
                          isTemplateSelected(template),
                      }"
                      :aria-checked="isTemplateSelected(template)"
                      tabindex="0"
                      @click="selectContractTemplate(template.id)"
                      @keydown.enter.prevent="
                        selectContractTemplate(template.id)
                      "
                      @keydown.space.prevent="
                        selectContractTemplate(template.id)
                      "
                    >
                      <span
                        class="contract-template-picker__indicator"
                        :class="{
                          'contract-template-picker__indicator--selected':
                            isTemplateSelected(template),
                        }"
                        aria-hidden="true"
                      ></span>
                      <span class="contract-template-picker__content">
                        <span class="contract-template-picker__name">
                          {{ templateDisplayName(template) }}
                        </span>
                        <span
                          v-if="templateDisplaySubtitle(template)"
                          class="contract-template-picker__subtitle"
                        >
                          {{ templateDisplaySubtitle(template) }}
                        </span>
                      </span>
                      <span
                        v-if="template.version"
                        class="contract-template-picker__version"
                      >
                        v{{ template.version }}
                      </span>
                    </div>
                  </div>
                  <oxd-text
                    v-if="contractTemplates.length"
                    tag="p"
                    class="contract-template-picker__error"
                  >
                    {{ contractTemplateValidationMessage }}
                  </oxd-text>
                </div>
                <oxd-grid
                  :cols="3"
                  class="orangehrm-full-width-grid contract-edit-panel__section"
                >
                  <oxd-grid-item class="contract-doc-num-field">
                    <oxd-input-field
                      v-model="contract.generatedDocNum"
                      :label="$t('pim.contract_document_number')"
                      :rules="rules.generatedDocNum"
                    />
                  </oxd-grid-item>
                </oxd-grid>
              </template>
              <oxd-form-actions class="contract-edit-panel__actions">
                <oxd-button
                  display-type="ghost"
                  :label="$t('general.cancel')"
                  @click="cancelContractEdit"
                />
                <submit-button />
              </oxd-form-actions>
            </div>
          </oxd-input-group>
        </oxd-form-row>
      </oxd-form>

      <template v-if="hasUpdatePermissions && !isEditingContract">
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.save')"
            type="submit"
            form="employee-job-form"
          />
        </oxd-form-actions>
      </template>
    </div>

    <oxd-divider v-if="hasUpdatePermissions && !isLoading" />

    <div
      v-if="hasUpdatePermissions && !isLoading"
      class="orangehrm-horizontal-padding orangehrm-vertical-padding"
    >
      <profile-action-header
        icon-name=""
        :display-type="terminationActionType"
        :label="terminationActionLabel"
        :title="terminationActionLabel"
        class="--termination-button"
        @click="onClickTerminate"
      >
        {{ $t('pim.employee_termination_activation') }}
        <oxd-text
          v-if="termination && termination.id"
          tag="p"
          class="orangehrm-terminate-date"
          @click="openTerminateModal"
        >
          {{ $t('pim.terminated_on') }}: {{ terminationDate }}
        </oxd-text>
      </profile-action-header>
    </div>
    <terminate-modal
      v-if="showTerminateModal"
      :employee-id="empNumber"
      :termination-reasons="terminationReasons"
      :termination-id="termination.id"
      @close="closeTerminateModal"
    ></terminate-modal>
  </edit-employee-layout>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import EditEmployeeLayout from '@/orangehrmPimPlugin/components/EditEmployeeLayout';
import JobSpecDownload from '@/orangehrmPimPlugin/components/JobSpecDownload';
import ProfileActionHeader from '@/orangehrmPimPlugin/components/ProfileActionHeader';
import TerminateModal from '@/orangehrmPimPlugin/components/TerminateModal';
import {OxdIcon, OxdSwitchInput} from '@ohrm/oxd';
import {
  required,
  maxFileSize,
  validFileTypes,
  validDateFormat,
  endDateShouldBeAfterStartDate,
} from '@ohrm/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useLocale from '@/core/util/composable/useLocale';

const jobDetailsModel = {
  joinedDate: '',
  jobLevelName: '',
  jobTitleId: [],
  empStatusId: [],
  jobCategoryId: [],
  subunitId: [],
  locationId: [],
};

const CONTRACT_TYPE_PERMANENT = 'permanent';
const CONTRACT_TYPE_CONTRACT = 'contract';

const CONTRACT_METHOD_UPLOAD_ONLY = 'replaceCurrent';
const CONTRACT_METHOD_UPLOAD_UPDATE_DETAILS = 'replaceCurrentUpdateDetails';
const CONTRACT_METHOD_RENEW_UPLOAD = 'replaceCurrentEmployeeUnsigned';
const CONTRACT_METHOD_RENEW_TEMPLATE = 'replaceUsingTemplate';

const contractDetailsModel = {
  contractType: null,
  startDate: '',
  endDate: '',
  oldAttachment: null,
  newAttachment: null,
  method: null,
  contractTemplateId: null,
  generatedDocNum: '',
};

export default {
  components: {
    'edit-employee-layout': EditEmployeeLayout,
    'oxd-switch-input': OxdSwitchInput,
    'job-spec-download': JobSpecDownload,
    'profile-action-header': ProfileActionHeader,
    'terminate-modal': TerminateModal,
    'oxd-icon': OxdIcon,
  },

  props: {
    empNumber: {
      type: String,
      required: true,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    jobTitles: {
      type: Array,
      default: () => [],
    },
    jobCategories: {
      type: Array,
      default: () => [],
    },
    subunits: {
      type: Array,
      default: () => [],
    },
    employmentStatuses: {
      type: Array,
      default: () => [],
    },
    terminationReasons: {
      type: Array,
      default: () => [],
    },
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/job-details`,
    );
    const httpEmploymentContract = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/employment-contract`,
    );
    const httpContractTemplates = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/contract-templates?isActive=true`,
    );
    const {jsDateFormat, userDateFormat} = useDateFormat();
    const {locale} = useLocale();

    return {
      http,
      httpEmploymentContract,
      locale,
      jsDateFormat,
      userDateFormat,
      httpContractTemplates,
    };
  },

  data() {
    return {
      CONTRACT_METHOD_RENEW_UPLOAD,
      CONTRACT_METHOD_RENEW_TEMPLATE,
      isLoading: false,
      showContractDetails: false,
      isContractSaving: false,
      isEditingContract: false,
      contractEditSnapshot: null,
      contractTemplateTouched: false,
      job: {...jobDetailsModel},
      contract: {...contractDetailsModel},
      termination: null,
      showTerminateModal: false,
      rules: {
        joinedDate: [validDateFormat(this.userDateFormat)],
        contractAction: [required],
        startDate: [
          (v) => {
            if (!this.showsContractMetadataFields) {
              return true;
            }
            return validDateFormat(this.userDateFormat)(v);
          },
        ],
        endDate: [
          (v) => {
            if (!this.showsContractMetadataFields || this.isPermanentContract) {
              return true;
            }
            return validDateFormat(this.userDateFormat)(v);
          },
          (v) => {
            if (!this.showsContractMetadataFields || this.isPermanentContract) {
              return true;
            }
            return endDateShouldBeAfterStartDate(() => this.contract.startDate)(
              v,
            );
          },
        ],
        contractAttachment: [
          (v) => {
            if (this.requiresContractFileUpload) {
              return required(v);
            }
            return true;
          },
          validFileTypes(this.allowedFileTypes),
          maxFileSize(this.maxFileSize),
        ],
        generatedDocNum: [
          (v) => {
            if (this.contract.method !== CONTRACT_METHOD_RENEW_TEMPLATE) {
              return true;
            }
            if (!required(v)) {
              return required(v);
            }
            if (!/^\d+$/.test(String(v).trim())) {
              return this.$t('general.invalid');
            }
            const num = parseInt(String(v).trim(), 10);
            if (num < 1) {
              return this.$t('general.invalid');
            }
            return true;
          },
        ],
      },
      contractTemplates: [],
      workShift: {
        name: null,
        startTime: null,
        endTime: null,
      },
    };
  },

  computed: {
    contractTypeOptions() {
      return [
        {
          id: CONTRACT_TYPE_PERMANENT,
          label: this.$t('pim.contract_type_permanent'),
        },
        {
          id: CONTRACT_TYPE_CONTRACT,
          label: this.$t('pim.contract_type_fixed_term'),
        },
      ];
    },
    isPermanentContract() {
      return this.contract.contractType?.id === CONTRACT_TYPE_PERMANENT;
    },
    contractActionOptions() {
      return [
        {
          id: CONTRACT_METHOD_UPLOAD_ONLY,
          label: this.$t('pim.contract_action_upload_only'),
          description: this.$t('pim.contract_action_upload_only_description'),
        },
        {
          id: CONTRACT_METHOD_UPLOAD_UPDATE_DETAILS,
          label: this.$t('pim.contract_action_upload_update_details'),
          description: this.$t(
            'pim.contract_action_upload_update_details_description',
          ),
        },
        {
          id: CONTRACT_METHOD_RENEW_UPLOAD,
          label: this.$t('pim.contract_action_renew_upload'),
          description: this.$t('pim.contract_action_renew_description'),
        },
        {
          id: CONTRACT_METHOD_RENEW_TEMPLATE,
          label: this.$t('pim.contract_action_renew_template'),
          description: this.$t('pim.contract_action_renew_description'),
        },
      ];
    },
    contractActionDescription() {
      return (
        this.contractActionOptions.find(
          (option) => option.id === this.contract.method,
        )?.description ?? ''
      );
    },
    showsContractMetadataFields() {
      if (!this.contract.method) {
        return false;
      }
      return this.contract.method !== CONTRACT_METHOD_UPLOAD_ONLY;
    },
    contractActionSelect: {
      get() {
        return (
          this.contractActionOptions.find(
            (option) => option.id === this.contract.method,
          ) ?? null
        );
      },
      set(option) {
        this.contract.method = option?.id ?? null;
        this.contract.newAttachment = null;
        this.contract.contractTemplateId = null;
        this.contractTemplateTouched = false;
        if (
          option?.id === CONTRACT_METHOD_UPLOAD_ONLY &&
          this.contractEditSnapshot
        ) {
          this.contract.contractType = this.contractEditSnapshot.contractType;
          this.contract.startDate = this.contractEditSnapshot.startDate;
          this.contract.endDate = this.contractEditSnapshot.endDate;
        }
      },
    },
    requiresContractFileUpload() {
      return (
        this.contract.method === CONTRACT_METHOD_UPLOAD_ONLY ||
        this.contract.method === CONTRACT_METHOD_UPLOAD_UPDATE_DETAILS ||
        this.contract.method === CONTRACT_METHOD_RENEW_UPLOAD
      );
    },
    contractTypeDisplay() {
      return this.contract.contractType?.label ?? '';
    },
    contractStartDateDisplay() {
      return this.formatContractDate(this.contract.startDate);
    },
    contractEndDateDisplay() {
      if (this.isPermanentContract) {
        return '—';
      }
      return this.formatContractDate(this.contract.endDate);
    },
    contractDocumentName() {
      return (
        this.contract.oldAttachment?.filename ?? this.$t('general.no_file')
      );
    },
    contractTemplateValidationMessage() {
      if (
        this.contract.method !== CONTRACT_METHOD_RENEW_TEMPLATE ||
        !this.contractTemplateTouched ||
        this.contract.contractTemplateId
      ) {
        return '';
      }
      return this.$t('general.required');
    },
    workShiftDisplay() {
      const {name, startTime, endTime} = this.workShift;
      if (!startTime || !endTime) {
        return '';
      }
      const times = `${startTime} - ${endTime}`;
      return name ? `${times} ${name}` : times;
    },
    selectedJobTitleId() {
      const jobTitleId = this.job.jobTitleId?.id;
      return jobTitleId || 0;
    },
    terminationActionLabel() {
      return this.termination?.id
        ? this.$t('pim.activate_employment')
        : this.$t('pim.terminate_employment');
    },
    terminationActionType() {
      return this.termination?.id ? 'ghost-success' : 'label-danger';
    },
    hasUpdatePermissions() {
      return this.$can.update(`job_details`);
    },
    normalizedJobTitles() {
      return this.jobTitles.map((jobTitle) => {
        return {
          id: jobTitle.id,
          label: jobTitle?.deleted
            ? jobTitle.label + this.$t('general.deleted')
            : jobTitle.label,
        };
      });
    },
    terminationDate() {
      return this.termination?.date
        ? formatDate(parseDate(this.termination.date), this.jsDateFormat, {
            locale: this.locale,
          })
        : null;
    },
  },

  watch: {
    'contract.contractType'(contractType) {
      if (contractType?.id === CONTRACT_TYPE_PERMANENT) {
        this.contract.endDate = '';
      }
    },
    showContractDetails(isVisible) {
      if (!isVisible && this.isEditingContract) {
        this.cancelContractEdit();
      }
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .getAll()
      .then((response) => {
        this.updateJobModel(response);
      })
      .then(() => {
        return this.httpEmploymentContract.getAll();
      })
      .then((response) => {
        this.updateContractModel(response);
      })
      .then(() => {
        return this.http.request({
          method: 'GET',
          url: `/api/v2/pim/employees/${this.empNumber}/work-shift`,
        });
      })
      .then((response) => {
        if (response) {
          this.updateWorkShiftModel(response);
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
    this.httpContractTemplates
      .request({
        method: 'GET',
        url: `/api/v2/pim/contract-templates?isActive=true`,
      })
      .then((response) => {
        this.contractTemplates = response.data.data;
      });
  },

  methods: {
    formatContractDate(value) {
      if (!value) {
        return '—';
      }
      return formatDate(parseDate(value), this.jsDateFormat, {
        locale: this.locale,
      });
    },

    downloadContract() {
      if (!this.contract.oldAttachment?.id) {
        return;
      }
      window.open(
        `${window.appGlobal.baseUrl}/pim/viewAttachment/empNumber/${this.empNumber}/attachId/${this.contract.oldAttachment.id}`,
        '_blank',
      );
    },

    cloneContractState() {
      return {
        contractType: this.contract.contractType,
        startDate: this.contract.startDate,
        endDate: this.contract.endDate,
        oldAttachment: this.contract.oldAttachment,
        newAttachment: null,
        method: null,
        contractTemplateId: null,
        generatedDocNum: this.contract.generatedDocNum,
      };
    },

    applyContractState(state) {
      this.contract.contractType = state.contractType;
      this.contract.startDate = state.startDate;
      this.contract.endDate = state.endDate;
      this.contract.oldAttachment = state.oldAttachment;
      this.contract.newAttachment = state.newAttachment;
      this.contract.method = state.method;
      this.contract.contractTemplateId = state.contractTemplateId;
      this.contract.generatedDocNum = state.generatedDocNum;
    },

    startContractEdit() {
      this.contractEditSnapshot = this.cloneContractState();
      this.contract.newAttachment = null;
      this.contract.method = null;
      this.contract.contractTemplateId = null;
      this.contractTemplateTouched = false;
      this.isEditingContract = true;
    },

    cancelContractEdit() {
      if (this.contractEditSnapshot) {
        this.applyContractState(this.contractEditSnapshot);
      }
      this.contractEditSnapshot = null;
      this.contractTemplateTouched = false;
      this.isEditingContract = false;
    },

    toTemplateId(id) {
      if (id == null || id === '') {
        return null;
      }
      const normalized = Number(id);
      return Number.isNaN(normalized) ? null : normalized;
    },

    selectContractTemplate(templateId) {
      this.contractTemplateTouched = true;
      this.contract.contractTemplateId = this.toTemplateId(templateId);
    },

    isTemplateSelected(template) {
      return (
        this.contract.contractTemplateId != null &&
        this.contract.contractTemplateId === this.toTemplateId(template.id)
      );
    },

    templateDisplayName(template) {
      return template.description?.trim() || template.contractName;
    },

    templateDisplaySubtitle(template) {
      const description = template.description?.trim();
      const contractName = template.contractName?.trim();

      if (description && contractName && description !== contractName) {
        return contractName;
      }

      return '';
    },

    getContractApiMethod() {
      if (this.contract.method === CONTRACT_METHOD_UPLOAD_UPDATE_DETAILS) {
        return CONTRACT_METHOD_UPLOAD_ONLY;
      }
      return this.contract.method;
    },

    formatContractDateForApi(value) {
      if (!value) {
        return null;
      }
      const parsed =
        parseDate(value, 'yyyy-MM-dd') ??
        parseDate(value, this.userDateFormat, {locale: this.locale});
      return parsed ? formatDate(parsed, 'yyyy-MM-dd') : value;
    },

    buildContractAttachmentPayload(attachment) {
      if (!attachment) {
        return undefined;
      }
      return {
        name: attachment.name,
        type: attachment.type,
        base64: attachment.base64,
        size: Number(attachment.size),
      };
    },

    buildContractSavePayload() {
      const useSnapshotMetadata =
        this.contract.method === CONTRACT_METHOD_UPLOAD_ONLY;
      const metadataSource =
        useSnapshotMetadata && this.contractEditSnapshot
          ? this.contractEditSnapshot
          : this.contract;
      const metadataIsPermanent =
        metadataSource.contractType?.id === CONTRACT_TYPE_PERMANENT;
      const generatedDocNum = String(
        this.contract.generatedDocNum ?? '',
      ).trim();

      return {
        contractType: metadataSource.contractType?.id,
        startDate: this.formatContractDateForApi(metadataSource.startDate),
        endDate: metadataIsPermanent
          ? null
          : this.formatContractDateForApi(metadataSource.endDate),
        currentContractAttachment: this.getContractApiMethod(),
        contractAttachment: this.buildContractAttachmentPayload(
          this.contract.newAttachment,
        ),
        ...(this.contract.contractTemplateId
          ? {contractTemplateId: this.contract.contractTemplateId}
          : {}),
        ...(this.contract.method === CONTRACT_METHOD_RENEW_TEMPLATE &&
        generatedDocNum
          ? {generatedDocNum}
          : {}),
      };
    },

    saveContract() {
      if (
        this.contract.method === CONTRACT_METHOD_RENEW_TEMPLATE &&
        !this.contract.contractTemplateId
      ) {
        this.contractTemplateTouched = true;
        return;
      }

      this.isContractSaving = true;
      this.httpEmploymentContract
        .update(0, this.buildContractSavePayload())
        .then((response) => {
          if (response) {
            this.updateContractModel(response);
          }
          this.contractEditSnapshot = null;
          this.isEditingContract = false;
          return this.$toast.updateSuccess();
        })
        .catch(() => {
          // Error toast is handled by the API service interceptor.
        })
        .finally(() => {
          this.isContractSaving = false;
        });
    },

    onSave() {
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            joinedDate: this.job.joinedDate,
            jobTitleId: this.job.jobTitleId?.id,
            jobCategoryId: this.job.jobCategoryId?.id,
            subunitId: this.job.subunitId?.id,
            empStatusId: this.job.empStatusId?.id,
            locationId: this.job.locationId?.id,
          },
        })
        .then((response) => {
          this.updateJobModel(response);
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.isLoading = false;
        });
    },

    onClickTerminate() {
      if (this.termination?.id) {
        this.isLoading = true;
        this.http
          .request({
            method: 'DELETE',
            url: `/api/v2/pim/employees/${this.empNumber}/terminations`,
          })
          .then(() => {
            return this.$toast.updateSuccess();
          })
          .then(() => {
            location.reload();
          });
      } else {
        this.openTerminateModal();
      }
    },

    openTerminateModal() {
      this.showTerminateModal = true;
    },

    closeTerminateModal(reload) {
      this.showTerminateModal = false;
      if (reload) {
        location.reload();
      }
    },

    updateContractModel(response) {
      const {data, meta} = response.data;
      const contractTypeId =
        data.contractType ??
        (data.endDate ? CONTRACT_TYPE_CONTRACT : CONTRACT_TYPE_PERMANENT);
      this.contract.contractType = this.contractTypeOptions.find(
        (option) => option.id === contractTypeId,
      );
      this.contract.startDate = data.startDate;
      this.contract.endDate = data.endDate;
      this.contract.oldAttachment = data.contractAttachment?.id
        ? data.contractAttachment
        : null;
      this.contract.newAttachment = null;
      this.contract.method = null;
      this.contract.contractTemplateId = null;
      this.contract.generatedDocNum = meta?.generatedDocNum ?? '';
      this.contractEditSnapshot = null;
      this.contractTemplateTouched = false;
      this.isEditingContract = false;
      this.showContractDetails = false;
    },

    updateWorkShiftModel(response) {
      const {data} = response.data;
      this.workShift.name = data.name ?? null;
      this.workShift.startTime = data.startTime ?? null;
      this.workShift.endTime = data.endTime ?? null;
    },

    updateJobModel(response) {
      const {data} = response.data;
      this.job.joinedDate = data.joinedDate;
      this.job.jobLevelName = data.jobTitle.levelName;
      this.job.jobTitleId = this.normalizedJobTitles.find(
        (item) => item.id === data.jobTitle?.id,
      );
      this.job.jobCategoryId = this.jobCategories.find(
        (item) => item.id === data.jobCategory?.id,
      );
      this.job.subunitId = this.subunits.find(
        (item) => item.id === data.subunit?.id,
      );
      this.job.empStatusId = this.employmentStatuses.find(
        (item) => item.id === data.empStatus?.id,
      );
      this.job.locationId = this.locations.find(
        (item) => item.id === data.location?.id,
      );
      this.termination = data.employeeTerminationRecord;
    },
  },
};
</script>

<style src="./employee.scss" lang="scss" scoped></style>
